import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authFetch, getCurrentUser } from '../utils/auth';
import PasswordStrengthField from '../components/security/PasswordStrengthField';
import { PASSWORD_MIN_LENGTH } from '../utils/passwordStrength';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Écran de réinitialisation OBLIGATOIRE du mot de passe au premier login
 * (flag force_password_reset_at_next_login). Plein écran, sans sidebar.
 */
const ForcePasswordResetPage = () => {
  const navigate = useNavigate();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [validity, setValidity] = useState({ tooShort: true, pwned: false, checking: false });
  const [saving, setSaving] = useState(false);
  const user = getCurrentUser();

  // Si l'utilisateur n'est pas authentifié, on renvoie vers /login.
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/login', { replace: true });
  }, [navigate]);

  const mismatch = confirm.length > 0 && pwd !== confirm;
  const canSubmit = !validity.tooShort && !validity.pwned && !validity.checking && pwd === confirm && pwd.length >= PASSWORD_MIN_LENGTH;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (pwd !== confirm) toast.error('Les deux mots de passe ne correspondent pas.');
      else if (validity.pwned) toast.error('Ce mot de passe est compromis, choisissez-en un autre.');
      else toast.error(`Minimum ${PASSWORD_MIN_LENGTH} caractères requis.`);
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/auth/force-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: pwd }),
      });
      const data = await res.json();
      if (res.ok) {
        // Met à jour l'objet user en localStorage (lève le drapeau) et récupère le rôle.
        let role;
        try {
          const u = getCurrentUser() || {};
          u.force_password_reset_at_next_login = false;
          role = u.role;
          localStorage.setItem('user', JSON.stringify(u));
        } catch { /* noop */ }
        toast.success('Mot de passe défini. Bienvenue !');
        // Redirection selon le rôle (le Super Admin va sur son panneau, pas sur le CRM agence).
        navigate(role === 'super_admin' ? '/super-admin' : '/', { replace: true });
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Erreur lors de la définition du mot de passe.');
      }
    } catch {
      toast.error('Erreur de connexion au serveur.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch { /* noop */ }
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center p-4" data-testid="force-reset-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-[#007BFF]/20 mb-4">
            <i className="bi bi-shield-lock text-2xl text-[#4DA3FF]"></i>
          </div>
          <h1 className="text-2xl font-bold text-white" data-testid="force-reset-title">Sécurisez votre compte</h1>
          <p className="text-slate-300 mt-2 text-sm">
            {user?.prenom
              ? `${user.prenom}, pour votre sécurité, vous devez définir un nouveau mot de passe avant de continuer.`
              : 'Pour votre sécurité, vous devez définir un nouveau mot de passe avant de continuer.'}
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="force-reset-form">
            <PasswordStrengthField
              value={pwd}
              onChange={setPwd}
              label="Nouveau mot de passe"
              testid="force-reset-new"
              onValidityChange={setValidity}
              autoFocus
              dark
            />
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">Confirmer le mot de passe</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-white/10 border border-white/20 rounded-xl py-3 px-4 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                data-testid="force-reset-confirm"
              />
              {mismatch && (
                <p className="text-xs text-red-300 mt-1.5" data-testid="force-reset-mismatch">
                  <i className="bi bi-exclamation-circle mr-1"></i>Les mots de passe ne correspondent pas.
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={saving || !canSubmit}
              className="w-full bg-[#28A745] hover:bg-[#218838] text-white font-semibold py-3 px-4 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="force-reset-submit"
            >
              {saving ? <><i className="bi bi-arrow-repeat animate-spin"></i>Enregistrement…</> : <><i className="bi bi-check2-circle"></i>Définir et continuer</>}
            </button>
          </form>
        </div>

        <button
          onClick={handleLogout}
          className="block mx-auto mt-6 text-slate-400 text-xs hover:text-white transition"
          data-testid="force-reset-logout"
        >
          Se déconnecter
        </button>
      </div>
    </div>
  );
};

export default ForcePasswordResetPage;
